// Notas fiscais (0.26): conversa com o servidor de notas (servidor-notas, função Node na Netlify),
// guarda o certificado A1 criptografado no cofre do aparelho, lê XML de NFS-e, NF-e e CT-e e monta as
// exportações (XML em .zip, planilha .xlsx e relatório em PDF).
import { zipSync } from 'fflate';
import { supabase } from './supabase';
import { DEMO } from './env';
import { getDoc, setDoc } from '../store/store';
import { cifrar, cofreAberto, decifrar } from './cofre';
import { salvarECompartilhar } from './arquivoSalvar';
import { compartilharXlsx } from './planilha';
import { gerarECompartilharPdf, nomeArquivo } from './pdf';
import { lerBase64 } from './anexos';
import { t } from './i18n';
import type { Anexo, Reg } from '../store/types';

export const so = (v: unknown) => String(v ?? '').replace(/\D/g, '');
export const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];
export type Ambiente = 'producao' | 'restrita';
export const ambienteDe = (empresa: Reg): Ambiente => (String(empresa.c.ambiente ?? '').startsWith('Produção (') ? 'producao' : 'restrita');

/** CNPJ/CPF com pontos e traço. */
export function mascaraDoc(v: unknown) {
  const d = so(v);
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  return String(v ?? '');
}

// ─── Servidor de notas ───────────────────────────────────────────────────────────────────────────
export const servidorNotas = () => String(getDoc('ajustes').notasServidor ?? '').trim().replace(/\/+$/, '');
export const setServidorNotas = (url: string) => setDoc('ajustes', d => ({ ...d, notasServidor: url.trim() }));

export type ErroGov = { codigo: string; descricao: string; complemento?: string };
export class ErroNotas extends Error { constructor(msg: string, public erros: ErroGov[] = []) { super(msg); } }

async function chamar<T = any>(acao: string, corpo: Record<string, unknown>): Promise<T> {
  if (DEMO) throw new ErroNotas(t('Na demonstração não há servidor de notas. Use o app com a sua conta.'));
  const base = servidorNotas();
  if (!base) throw new ErroNotas(t('Falta ligar o servidor de notas: no computador, dê dois cliques em 13-LIGAR-NOTAS.bat e cole aqui o endereço que ele mostrar.'));
  const { data } = await supabase!.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ErroNotas(t('Entre na sua conta para usar as notas fiscais.'));
  let r: Response;
  try {
    r = await fetch(`${base}/api/fiscal`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ acao, ...corpo }) });
  } catch { throw new ErroNotas(t('Sem conexão com o servidor de notas. Confira a internet e o endereço do servidor.')); }
  const j = await r.json().catch(() => null);
  if (!j) throw new ErroNotas(t('O servidor de notas respondeu algo inesperado (HTTP {s}). Confira o endereço.', { s: r.status }));
  if (j.ok === false && j.erros?.length && !('status' in j) && r.status >= 400) throw new ErroNotas(j.erros.map((e: ErroGov) => e.descricao).join('\n'), j.erros);
  return j as T;
}
export async function testarServidor(url: string) {
  const r = await fetch(`${url.trim().replace(/\/+$/, '')}/api/fiscal`).then(x => x.json()).catch(() => null);
  return !!r?.ok;
}

// ─── Certificado A1 (no cofre do aparelho, criptografado com a senha do cofre) ──────────────────
export type InfoCert = { titular: string; documento: string; validade: string; emissor: string };
export async function conferirCertificado(pfxB64: string, senha: string) {
  return chamar<InfoCert>('certificado', { pfx: pfxB64, senha });
}
/** Campos da empresa com o certificado: o arquivo e a senha só ficam criptografados (cofre). */
export function camposDoCertificado(pfxB64: string, senha: string, info: InfoCert) {
  return { _pfx: cifrar(pfxB64), _senha: cifrar(senha), certTitular: info.titular, certDoc: info.documento, certValidade: info.validade.slice(0, 10), certEmissor: info.emissor };
}
function credenciais(empresa: Reg) {
  if (!empresa.c._pfx) throw new ErroNotas(t('Ligue o certificado A1 da empresa primeiro.'));
  if (!cofreAberto()) throw new ErroNotas('cofre-fechado');
  const pfx = decifrar(empresa.c._pfx), senha = decifrar(empresa.c._senha);
  if (pfx === null || senha === null) throw new ErroNotas(t('Não foi possível abrir o certificado guardado. Ligue o certificado de novo.'));
  return { pfx, senha, ambiente: ambienteDe(empresa) };
}

// ─── Emissão da NFS-e ───────────────────────────────────────────────────────────────────────────
export const OP_SIMPLES: Record<string, string> = { 'Não optante pelo Simples': '1', 'MEI': '2', 'ME ou EPP do Simples': '3' };
export type DadosEmissao = { tomador: Reg | null; servico: { descricao: string; cTribNac: string; cNBS?: string; cTribMun?: string; cLocPrestacao: string };
  valor: number; desconto?: number; competencia: string; issRetido?: boolean; aliquota?: number };
export type ResultadoEmissao = { ok: boolean; chave?: string; xml?: string; resumo?: ResumoXml & Record<string, any>; erros?: ErroGov[]; idDps?: string };

export async function emitirNfse(empresa: Reg, d: DadosEmissao): Promise<ResultadoEmissao> {
  const c = empresa.c, tom = d.tomador?.c;
  const dps = {
    serie: c.serie || '1', nDPS: String(Math.max(1, Number(c.proximo) || 1)), dCompet: d.competencia,
    prestador: { documento: so(c.documento), im: c.im, email: c.email, fone: c.fone, cLocEmi: so(c.cMun), opSimpNac: OP_SIMPLES[c.regime] ?? '1', regApTribSN: '1', regEspTrib: '0' },
    tomador: tom ? { documento: so(tom.documento), nome: tom.nome, email: tom.email, fone: tom.fone,
      end: so(tom.cep) ? { cMun: so(tom.cMun), cep: so(tom.cep), xLgr: tom.logradouro, nro: tom.numero || 'S/N', xCpl: tom.complemento, xBairro: tom.bairro } : undefined } : undefined,
    servico: { cLocPrestacao: so(d.servico.cLocPrestacao || c.cMun), cTribNac: so(d.servico.cTribNac), cTribMun: d.servico.cTribMun, cNBS: d.servico.cNBS, xDescServ: d.servico.descricao },
    valores: { vServ: d.valor, vDescIncond: d.desconto || 0 },
    iss: { tribISSQN: '1', tpRetISSQN: d.issRetido ? '2' : '1', pAliq: d.aliquota ?? c.aliquota },
    totTrib: { pTotTribSN: c.pTotTribSN },
  };
  return chamar<ResultadoEmissao>('emitir', { ...credenciais(empresa), dps });
}
export const consultarNfse = (empresa: Reg, chave: string) => chamar<{ ok: boolean; xml?: string; erros?: ErroGov[] }>('consultar', { ...credenciais(empresa), chave });
export const danfse = (empresa: Reg, chave: string) => chamar<{ ok: boolean; pdf?: string; erros?: ErroGov[] }>('danfse', { ...credenciais(empresa), chave });
export const cancelarNfse = (empresa: Reg, chave: string, cMotivo: string, xMotivo: string) =>
  chamar<{ ok: boolean; xmlEvento?: string; erros?: ErroGov[] }>('cancelar', { ...credenciais(empresa), chave, cMotivo, xMotivo });

// ─── Captura das notas emitidas e recebidas ─────────────────────────────────────────────────────
export type DocCapturado = { nsu: string | number; chave: string; tipo: string; evento?: string | null; schema?: string; xml: string; resumo: any };
export const capturarNfse = (empresa: Reg, ultNSU: number) =>
  chamar<{ ok: boolean; status: string; ultNSU: number; docs: DocCapturado[]; avisos: string[]; erros?: ErroGov[] }>('capturar_nfse', { ...credenciais(empresa), ultNSU });
export const capturarNfe = (empresa: Reg, ultNSU: string) =>
  chamar<{ ok: boolean; cStat: string; xMotivo: string; ultNSU: string; maxNSU: string; docs: DocCapturado[] }>('capturar_nfe', { ...credenciais(empresa), uf: empresa.c.uf, ultNSU });
export const cienciaNfe = (empresa: Reg, chave: string) => chamar<{ cStat: string; xMotivo: string }>('ciencia_nfe', { ...credenciais(empresa), chave });

// ─── Leitura de XML (importar ou capturar) ──────────────────────────────────────────────────────
export type ResumoXml = { tipo: 'NFS-e' | 'NF-e' | 'CT-e' | 'Outro'; chave: string; numero: string; data: string; valor: number; iss: number;
  emitente: { documento: string; nome: string }; destinatario: { documento: string; nome: string }; descricao: string; cancelada?: boolean };
const pega = (tag: string, x: string) => (x.match(new RegExp(`<(?:\\w+:)?${tag}\\b[^>]*>([^<]*)</(?:\\w+:)?${tag}>`)) ?? [])[1] ?? '';
const bloco = (tag: string, x: string) => (x.match(new RegExp(`<(?:\\w+:)?${tag}\\b[^>]*>([\\s\\S]*?)</(?:\\w+:)?${tag}>`)) ?? [])[1] ?? '';
const desEsc = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');
const pessoa = (x: string) => ({ documento: pega('CNPJ', x) || pega('CPF', x), nome: desEsc(pega('xNome', x)) });

export function lerXml(xml: string): ResumoXml | null {
  if (/<(?:\w+:)?infNFSe\b/.test(xml)) {
    const dps = bloco('infDPS', xml);
    return { tipo: 'NFS-e', chave: (xml.match(/Id="NFS(\d{50})"/) ?? [])[1] ?? '', numero: pega('nNFSe', xml), data: (pega('dhProc', xml) || pega('dhEmi', xml) || pega('dCompet', xml)).slice(0, 10),
      valor: Number(pega('vServ', xml)) || 0, iss: Number(pega('vISSQN', xml)) || 0,
      emitente: pessoa(bloco('emit', xml) || bloco('prest', dps)), destinatario: pessoa(bloco('toma', dps || xml)), descricao: desEsc(pega('xDescServ', xml)) };
  }
  if (/<(?:\w+:)?infNFe\b/.test(xml)) {
    const ide = bloco('ide', xml);
    return { tipo: 'NF-e', chave: (xml.match(/Id="NFe(\d{44})"/) ?? [])[1] ?? '', numero: pega('nNF', ide), data: (pega('dhEmi', ide) || pega('dEmi', ide)).slice(0, 10),
      valor: Number(pega('vNF', xml)) || 0, iss: Number(pega('vISSQN', xml)) || 0, emitente: pessoa(bloco('emit', xml)), destinatario: pessoa(bloco('dest', xml)),
      descricao: desEsc(pega('xProd', xml)) };
  }
  if (/<(?:\w+:)?resNFe\b/.test(xml)) {
    return { tipo: 'NF-e', chave: pega('chNFe', xml), numero: '', data: pega('dhEmi', xml).slice(0, 10), valor: Number(pega('vNF', xml)) || 0, iss: 0,
      emitente: pessoa(xml), destinatario: { documento: '', nome: '' }, descricao: '', cancelada: pega('cSitNFe', xml) === '3' };
  }
  if (/<(?:\w+:)?infCte\b/.test(xml)) {
    return { tipo: 'CT-e', chave: (xml.match(/Id="CTe(\d{44})"/) ?? [])[1] ?? '', numero: pega('nCT', xml), data: pega('dhEmi', xml).slice(0, 10),
      valor: Number(pega('vTPrest', xml)) || 0, iss: 0, emitente: pessoa(bloco('emit', xml)), destinatario: pessoa(bloco('dest', xml) || bloco('rem', xml)), descricao: desEsc(pega('xOutCat', xml) || pega('natOp', xml)) };
  }
  return null;
}

/** Campos da ficha "nota" a partir do XML lido; "minha" = CNPJ/CPF da empresa (para saber se foi emitida ou recebida). */
export function camposDaNota(r: ResumoXml, minha: string, completo = true) {
  const emitida = so(r.emitente.documento) === so(minha);
  const outro = emitida ? r.destinatario : r.emitente;
  return {
    direcao: emitida ? 'Emitida' : 'Recebida', tipo: r.tipo, numero: r.numero, chave: r.chave, data: r.data, valor: r.valor, iss: r.iss || '',
    contraparte: outro.nome, contraparteDoc: outro.documento, descricao: r.descricao.slice(0, 500),
    status: r.cancelada ? 'Cancelada' : completo ? (emitida ? 'Emitida' : 'Recebida') : 'Resumo (falta o XML completo)',
  };
}

// ─── Exportações ────────────────────────────────────────────────────────────────────────────────
const baixar = (b64: string) => Uint8Array.from(atob(b64), ch => ch.charCodeAt(0));
const xmlDe = (r: Reg): Anexo | undefined => (r.anexos ?? []).find(a => a.mime === 'application/xml' || /\.xml$/i.test(a.nome));

/** Todos os XML das notas num .zip (o que o contador pede). */
export async function exportarZip(notas: Reg[], nome: string) {
  const arquivos: Record<string, Uint8Array> = {};
  let faltam = 0;
  for (const n of notas) {
    const a = xmlDe(n);
    if (!a) { faltam++; continue; }
    try { arquivos[nomeArquivo(`${n.c.tipo}-${n.c.direcao}-${n.c.numero || ''}-${n.c.chave || n.id}.xml`)] = baixar(await lerBase64(a)); }
    catch { faltam++; }
  }
  if (!Object.keys(arquivos).length) throw new ErroNotas(t('Nenhum XML neste período (ou foram guardados em outro dispositivo).'));
  await salvarECompartilhar(zipSync(arquivos, { level: 6 }), nomeArquivo(nome + '.zip'), 'application/zip');
  return faltam;
}

const colunas = ['Data', 'Tipo', 'Emitida/Recebida', 'Número', 'Chave de acesso', 'CNPJ/CPF', 'Nome', 'Descrição', 'Valor', 'ISS', 'Situação'];
const linhaDe = (n: Reg) => [n.c.data || '', n.c.tipo || '', n.c.direcao || '', n.c.numero || '', n.c.chave || '', mascaraDoc(n.c.contraparteDoc), n.c.contraparte || '',
  n.c.descricao || '', Number(n.c.valor) || 0, Number(n.c.iss) || 0, n.c.status || ''];

/** Planilha .xlsx de verdade (abre no Excel, no Google Planilhas e no Numbers). */
export async function exportarXlsx(notas: Reg[], nome: string) {
  await compartilharXlsx(nome, [{ nome: 'Notas', colunas, linhas: notas.map(linhaDe), larguras: [12, 12, 12, 12, 50, 20, 36, 36, 14, 14, 18] }]);
}

/** Relatório das notas em PDF, com os totais. */
export async function exportarPdf(notas: Reg[], titulo: string, empresa: Reg, din: (v: number) => string) {
  const tot = (dir: string) => notas.filter(n => n.c.direcao === dir && n.c.status !== 'Cancelada').reduce((s, n) => s + (Number(n.c.valor) || 0), 0);
  await gerarECompartilharPdf(titulo, [
    { t: 'titulo', texto: titulo },
    { t: 'sub', texto: `${empresa.c.nome || ''} · ${mascaraDoc(empresa.c.documento)}` },
    { t: 'par', texto: t('Emitidas: {v} · Recebidas: {r} · {n} nota(s)', { v: din(tot('Emitida')), r: din(tot('Recebida')), n: notas.length }), negrito: true },
    { t: 'tabela', colunas: [t('Data'), t('Tipo'), t('Nº'), t('Nome'), t('Valor'), t('Situação')], larguras: [0.13, 0.12, 0.1, 0.37, 0.14, 0.14],
      linhas: notas.map(n => [String(n.c.data || '').split('-').reverse().join('/'), `${n.c.tipo} · ${t(n.c.direcao === 'Emitida' ? 'emitida' : 'recebida')}`, String(n.c.numero || ''), String(n.c.contraparte || ''), din(Number(n.c.valor) || 0), String(n.c.status || '')]) },
  ], 'My Planner');
}
export { baixar as deBase64 };
